import React, { useEffect } from 'react'
import TopNavBar from './TopNavBar'
import BottomNavBar from './BottomNavBar'
import PrimarySidebarNav from './PrimarySidebarNav'
import UploadDrawer from '../../components/UploadDrawer'
import BlogDashboard from '../../features/blog/dashboard/BlogDashboard'
import LoadingComponent from './LoadingComponents'
import { useStore } from '../stores/store'
import { observer } from 'mobx-react-lite'
import MotdBanner from '../common/ui/MotdBanner'
import Homepage from '../../features/home/Homepage'
import PostForm from '../../features/blog/form/PostForm'
import PostDetails from '../../features/blog/details/PostDetails'
import GunVaultPage from '../../features/gunvault/GunVaultPage'
import FirearmDetails from '../../features/firearms/details/FirearmDetails'
import FirearmForm from '../../features/firearms/form/FirearmForm'
import Inventory from '../../features/inventory/Inventory'
import InventoryDetail from '../../features/inventory/InventoryDetail'
import PartsBin from '../../features/parts/PartsBin'
import PartDetail from '../../features/parts/PartDetail'
import { Route, Switch, useLocation, Redirect } from 'react-router-dom'
import ArduinoDashboard from '../../features/arduinos/dashboard/ArduinoDashboard'
import ArduinoDetails from '../../features/arduinos/dashboard/ArduinoDetails'
import ArduinoForm from '../../features/arduinos/form/ArduinoForm'
import TestErrors from '../../features/errors/TestError'
import { ToastContainer } from 'react-toastify'
import NotFound from '../../features/errors/NotFound'
import ServerError from '../../features/errors/ServerError'
import LoginForm from '../../features/users/LoginForm'
import RegisterForm from '../../features/users/RegisterForm'
import ForgotPasswordForm from '../../features/users/ForgotPasswordForm'
import ResetPasswordForm from '../../features/users/ResetPasswordForm'
import ModalContainer from '../common/modals/ModalContainer'
import ProfilePage from '../../features/profiles/ProfilePage'
import ProfileEditForm from '../../features/profiles/ProfileEditForm'
import RegistryDashboard from '../../features/registry/RegistryDashboard'
import RegistryForm from '../../features/registry/RegistryForm'
import RegistryFullForm from '../../features/registry/RegistryFullForm'
import RegistryModelDetails from '../../features/registry/RegistryModelDetails'
import BuildsDashboard from '../../features/builds/BuildsDashboard'
import BuildForm from '../../features/builds/BuildForm'
import BuildWorkbench from '../../features/builds/BuildWorkbench'
import ModerationDashboard from '../../features/moderation/ModerationDashboard'
import LegacyFollowingFeedPage from '../../features/feed/LegacyFollowingFeedPage'
import AdminUsersPage from '../../features/admin/AdminUsersPage'
import AdminUserDetailPage from '../../features/admin/AdminUserDetailPage'
import AdminContentPage from '../../features/admin/AdminContentPage'
import OverseerPage from '../../features/admin/OverseerPage'
import SearchResultsPage from '../../features/search/SearchResultsPage'
import DiscoverPage from '../../features/profiles/DiscoverPage'
import NotificationsPage from '../../features/notifications/NotificationsPage'
import AmmoDashboard from '../../features/ammo/AmmoDashboard'
import AmmoProductDetail from '../../features/ammo/AmmoProductDetail'
import CartridgeSpecDetail from '../../features/ammo/CartridgeSpecDetail'
import SubmitAmmoPage from '../../features/ammo/SubmitAmmoPage'
import ReloaderForum from '../../features/ammo/ReloaderForum'
import RecipeDetail from '../../features/ammo/RecipeDetail'
import SubmitRecipePage from '../../features/ammo/SubmitRecipePage'
import SlopFeedPage from '../../features/slopfeed/SlopFeedPage'
import SlopPostPage from '../../features/slopfeed/SlopPostPage'
import ShootDetailPage from '../../features/slopfeed/ShootDetailPage'
import AppShellLayout from './AppShellLayout'
import useMediaQueryMatch from '../hooks/useMediaQueryMatch'
import UiShowcase from '../../features/dev/UiShowcase'
import AboutUsPage from '../../features/about/AboutUsPage'
import { applyPageSeo } from '../utils/pageSeo'

function App () {
  const location = useLocation()
  const { commonStore, userStore, navigationStore, uploadDrawerStore } = useStore()
  const desktopNav = useMediaQueryMatch('(min-width: 769px)')
  useEffect(() => {
    if (commonStore.token) {
      Promise.all([
        userStore.getUser(),
        commonStore.loadMotd(),
        commonStore.loadFeatureFlags()
      ]).finally(() => commonStore.setAppLoaded())
    } else {
      Promise.all([
        commonStore.loadMotd(),
        commonStore.loadFeatureFlags()
      ]).finally(() => commonStore.setAppLoaded())
    }
  }, [commonStore, userStore])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    navigationStore.syncPrimaryFromLocation(location.pathname, userStore.user?.username)
  }, [location.pathname, userStore.user?.username, navigationStore])

  useEffect(() => {
    applyPageSeo(location.pathname)
  }, [location.pathname])

  if (!commonStore.appLoaded) {
    return <LoadingComponent content="Loading app..." />
  }

  return (
    <>
      <ToastContainer position="bottom-right" hideProgressBar />
      <ModalContainer />
      <Switch>
        <Route exact path="/">
          {userStore.isLoggedIn
            ? (
            <Redirect to="/slop-feed" />
              )
            : (
            <Homepage />
              )}
        </Route>
        <Route path="/">
          <>
            <TopNavBar
              onUploadClick={() => uploadDrawerStore.open()}
              onSettingsClick={() => {
                /* reserved for dedicated settings route / modal */
              }}
            />
            <PrimarySidebarNav />
            <BottomNavBar />
            <UploadDrawer />

            <AppShellLayout desktopNav={desktopNav}>
              <MotdBanner />
              <Switch>
                <Route exact path="/blog" component={BlogDashboard} />
                <Route path="/blog/:id" component={PostDetails} />
                <Route
                  key={location.key}
                  path={['/createPost', '/manage/:id']}
                  component={PostForm}
                />
                <Route exact path="/vault" render={() => <Redirect to="/gun-vault" />} />
                <Route exact path="/gun-vault" component={GunVaultPage} />
                <Route path="/gun-vault/:id" component={FirearmDetails} />
                <Route exact path="/ammo" component={AmmoDashboard} />
                <Route exact path="/ammo/submit" component={SubmitAmmoPage} />
                <Route path="/ammo/products/:id" component={AmmoProductDetail} />
                <Route path="/ammo/specs/:id" component={CartridgeSpecDetail} />
                <Route exact path="/ammo/reloading" component={ReloaderForum} />
                <Route exact path="/ammo/reloading/new" component={SubmitRecipePage} />
                <Route path="/ammo/reloading/:id" component={RecipeDetail} />
                <Route exact path="/slop-feed/shoot/:shootId" component={ShootDetailPage} />
                <Route exact path="/slop-feed/:id" component={SlopPostPage} />
                <Route exact path="/slop-feed" component={SlopFeedPage} />
                <Route exact path="/inventory" component={Inventory} />
                <Route path="/inventory/:id" component={InventoryDetail} />
                <Route exact path="/parts-bin" component={PartsBin} />
                <Route exact path="/parts/:id" component={PartDetail} />
                <Route exact path="/registry" component={RegistryDashboard} />
                <Route exact path="/registry/new" component={RegistryForm} />
                <Route exact path="/registry/full-form" component={RegistryFullForm} />
                <Route path="/registry/:id" component={RegistryModelDetails} />
                <Route exact path="/builds" component={BuildsDashboard} />
                <Route exact path="/builds/new" component={BuildForm} />
                <Route path="/builds/:id/edit" render={({ match }) => <Redirect to={`/builds/${match.params.id}`} />} />
                <Route path="/builds/:id" component={BuildWorkbench} />
                <Route
                  key={location.key}
                  path={['/createFirearm', '/manageFirearm/:id']}
                  component={FirearmForm}
                />
                <Route
                  exact
                  path="/profile"
                  render={() =>
                    userStore.isLoggedIn && userStore.user
                      ? (
                      <Redirect to={`/profiles/${userStore.user.username}`} />
                        )
                      : (
                      <Redirect to="/login" />
                        )
                  }
                />
                <Route exact path="/profiles/:username" component={ProfilePage} />
                <Route path="/profiles/:username/edit" component={ProfileEditForm} />
                <Route exact path="/feed" render={() => <Redirect to="/legacy-following-feed" />} />
                <Route exact path="/legacy-following-feed" component={LegacyFollowingFeedPage} />
                <Route exact path="/moderation" component={ModerationDashboard} />
                <Route exact path="/admin/users" component={AdminUsersPage} />
                <Route path="/admin/users/:username" component={AdminUserDetailPage} />
                <Route exact path="/admin/overseer" component={OverseerPage} />
                <Route exact path="/admin/content" component={AdminContentPage} />
                <Route path="/errors" component={TestErrors} />
                <Route path="/server-error" component={ServerError} />
                <Route path="/login" component={LoginForm} />
                <Route path="/register" component={RegisterForm} />
                <Route exact path="/about" component={AboutUsPage} />
                <Route path="/search" component={SearchResultsPage} />
                <Route path="/discover" component={DiscoverPage} />
                <Route path="/notifications" component={NotificationsPage} />
                <Route exact path="/ui-showcase" component={UiShowcase} />
                <Route path="/forgot-password" component={ForgotPasswordForm} />
                <Route path="/reset-password" component={ResetPasswordForm} />
                <Route
                  exact
                  path="/arduinos"
                  render={() => (userStore.user?.isAdmin ? <ArduinoDashboard /> : <Redirect to="/" />)}
                />
                <Route
                  path="/arduinos/:id"
                  render={(props) => (userStore.user?.isAdmin ? <ArduinoDetails {...props} /> : <Redirect to="/" />)}
                />
                <Route
                  key={location.hash}
                  path={['/createArduino/', '/editArduino/:id']}
                  render={(props) => (userStore.user?.isAdmin ? <ArduinoForm {...props} /> : <Redirect to="/" />)}
                />
                <Route component={NotFound} />
              </Switch>
            </AppShellLayout>
          </>
        </Route>
      </Switch>
    </>
  )
}

export default observer(App)
